import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { PostComposer, type PostPreset } from '@components/social/PostComposer';

/**
 * Compositor de post global (como o ActivityModalContext): grade, calendário, lista,
 * aprovações, sino, atividade e biblioteca abrem a mesma janela. `version` incrementa a cada
 * alteração salva — as telas o incluem nas dependências do carregamento.
 */
interface SocialPostContextType {
  openPost: (id: number) => void;
  openNewPost: (preset?: PostPreset) => void;
  version: number;
}

const SocialPostContext = createContext<SocialPostContextType | undefined>(undefined);

// `key` fica igual quando o post novo é salvo pela 1ª vez: o compositor não é remontado.
type State = { mode: 'edit'; id: number; key: string } | { mode: 'create'; preset?: PostPreset; key: string } | null;

export function SocialPostProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<State>(null);
  const [version, setVersion] = useState(0);

  const openPost = useCallback((id: number) => setState({ mode: 'edit', id, key: `edit-${id}` }), []);
  const openNewPost = useCallback((preset?: PostPreset) => setState({ mode: 'create', preset, key: `create-${Date.now()}` }), []);
  const close = useCallback(() => setState(null), []);
  // Post novo salvo pela 1ª vez: o modal passa a editar o post criado (mesma instância).
  const changed = useCallback((savedId?: number) => {
    setVersion((v) => v + 1);
    if (savedId) setState((s) => (s && s.mode === 'create' ? { mode: 'edit', id: savedId, key: s.key } : s));
  }, []);

  const value = useMemo(() => ({ openPost, openNewPost, version }), [openPost, openNewPost, version]);

  return (
    <SocialPostContext.Provider value={value}>
      {children}
      {state && (
        <PostComposer
          key={state.key}
          postId={state.mode === 'edit' ? state.id : undefined}
          preset={state.mode === 'create' ? state.preset : undefined}
          onClose={close}
          onChanged={changed}
        />
      )}
    </SocialPostContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSocialPost(): SocialPostContextType {
  const context = useContext(SocialPostContext);
  if (context === undefined) {
    throw new Error('useSocialPost deve ser usado dentro de um SocialPostProvider');
  }
  return context;
}
